import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  AiTrainingsJobInformationUrl,
  AiTrainingsJobLogsUrl,
  AiTrainingsUrl,
} from "../../utils/urls";
import RightArrow from "./../../assets/icons/arrow-right.svg?react";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Helmet } from "react-helmet";
import { Skeleton, TabOptions, Tabs, Typography } from "djuno-design";
import NoticeIcon from "./../../assets/icons/logs/notice.svg?react";
import ChainIcon from "./../../assets/icons/chain.svg?react";
import { AiJobsRefreshStatus } from "../../store/ai/jobs/jobsSlice";
import {
  getAiJobAsync,
  handleClearAiJobSlice,
  selectAiJob,
  selectAiJobLoading,
} from "../../store/ai/jobs/jobSlice";
import { LogTerminalProvider } from "../../components/general/LogTerminal";

export const generateAiJobRoutes = (jobId: string) => {
  const options: TabOptions = [];

  const jobInformationOption = {
    label: (
      <div className="flex items-center gap-1">
        <NoticeIcon className="w-3" />
        Job information
      </div>
    ),
    url: AiTrainingsJobInformationUrl(jobId),
    testId: "1th-tab",
  };
  const attachedDataOption = {
    label: (
      <div className="flex items-center gap-1">
        <ChainIcon className="w-3" />
        Logs
      </div>
    ),
    url: AiTrainingsJobLogsUrl(jobId),
    testId: "2th-tab",
  };

  options.push(jobInformationOption);
  options.push(attachedDataOption);

  return options;
};

const AiJobPage = () => {
  const { jobId, engine } = useParams();

  const job = useAppSelector(selectAiJob);
  const jobLoading = useAppSelector(selectAiJobLoading);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const AiJobPageMenus = useMemo(() => {
    if (jobId) {
      return generateAiJobRoutes(jobId);
    }
    return [];
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      dispatch(getAiJobAsync({ id: jobId }));
    }

    return () => {
      dispatch(handleClearAiJobSlice());
    };
  }, [dispatch, jobId, engine]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (
      job &&
      AiJobsRefreshStatus.includes(job.status.state as unknown as string)
    ) {
      interval = setInterval(() => {
        dispatch(
          getAiJobAsync({
            id: job.id,
            withoutLoading: true,
          })
        );
      }, 20000);
    } else {
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, job]);

  return (
    <LogTerminalProvider>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | AI Trainings (
          {job ? job.spec.name : ""})
        </title>
        <meta name="description" content="" />
      </Helmet>
      <div className="flex items-center justify-between h-16">
        <div className="px-6 items-center justify-between flex flex-1 transition duration-150">
          <div className="flex items-center gap-2">
            <RightArrow
              onClick={() => {
                navigate(AiTrainingsUrl);
              }}
              className="rotate-180 w-5 h-5 hover:scale-110 transition-all duration-500 text-slate-800 dark:text-slate-100 cursor-pointer"
            />
            {job && !jobLoading && (
              <>
                <div className="flex flex-col">
                  <Typography.Title level={5} className="!mb-0">
                    {job.spec.name}
                  </Typography.Title>
                </div>
              </>
            )}
            {jobLoading && (
              <>
                <Skeleton
                  shape="rectangle"
                  style={{ width: 200, height: 30 }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 mt-5">
        <div className="h-11">
          <Tabs options={AiJobPageMenus} />
        </div>
        <div className="py-6">
          <Outlet />
        </div>
      </div>
    </LogTerminalProvider>
  );
};
export default AiJobPage;
